import { Formik } from 'formik'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerUploadInsurance, CustomerUploadUpdatedInsurance } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CustomerInsuranceFormValues } from './CustomerInsuranceForm.interface'
import { CustomerOnboardingInsuranceSchema } from './CustomerInsuranceForm.validator'
import tw from 'twin.macro'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { ChecklistAlternateMajor } from '@shopify/polaris-icons'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import { getNotCompletedCustomerOnboardingCollectDataIdByType } from 'src/selectors/customer'
import {
  CustomerCollectedDataStatus,
  CustomerCollectedDataType,
} from 'src/constants/customer/enums'
import { CustomerFormHeader } from '../shared/CustomerFormHeader'
import { CustomerCollectedData } from 'src/interfaces/customer'

const initialValues: CustomerInsuranceFormValues = {
  file: '',
}

interface Props {
  stepNumber?: number
  stepCount?: number
  collectedData?: CustomerCollectedData
  onSubmit?: () => void
}

export const CustomerInsuranceForm = ({
  stepNumber,
  stepCount,
  collectedData,
  onSubmit,
}: Props) => {
  const dispatch = useDispatch()

  const notCompletedCustomerOnboardingCollectDataId = useSelector(
    getNotCompletedCustomerOnboardingCollectDataIdByType(CustomerCollectedDataType.Insurance),
  )

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async (values: CustomerInsuranceFormValues) => {
    setIsLoading(true)

    try {
      if (collectedData?.status !== CustomerCollectedDataStatus.Created) {
        await createAsyncAction(
          dispatch,
          CustomerUploadUpdatedInsurance.request({
            ...values,
            collectedDataId: collectedData?.id,
          }),
        )
      } else {
        await createAsyncAction(
          dispatch,
          CustomerUploadInsurance.request({
            ...values,
            collectedDataId: notCompletedCustomerOnboardingCollectDataId,
          }),
        )
      }

      showToast('Insurance submitted successfully', {
        variant: 'success',
      })

      if (onSubmit) {
        onSubmit()
      }
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={CustomerOnboardingInsuranceSchema}
    >
      {({ handleSubmit }) => (
        <div css={tw`w-[318px] bg-white rounded-lg flex-1 flex flex-col justify-between`}>
          <div>
            <CustomerFormHeader
              title={stepCount ? undefined : 'Resubmit info'}
              stepCount={stepCount}
              stepNumber={stepNumber}
            />
            <div css={tw`px-4 pt-6 py-2`}>
              <ChecklistAlternateMajor width={20} />
              <Typography variant="h4" css={tw`mt-2 mb-2`}>
                Commercial insurance
              </Typography>
              <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
              <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-2 mt-2`}>
                Please upload proof of your commercial insurance coverage. Carriers may require
                multiple types of coverage before approving your onboarding. If you carry any of the
                following, be sure to upload documentation for each:
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                &nbsp;&nbsp;•&nbsp;&nbsp; Commercial Auto Insurance
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                &nbsp;&nbsp;•&nbsp;&nbsp; Cargo Insurance
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                &nbsp;&nbsp;•&nbsp;&nbsp; Commercial General Liability
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4`}>
                &nbsp;&nbsp;•&nbsp;&nbsp; Workers' Compensation
              </Typography>
              <div css={tw`flex flex-row`}>
                <div css={tw`bg-[#FF6320] rounded-2xl w-4 mr-3`} />
                <div>
                  <Typography variant="h5" css={tw`mb-1`}>
                    Tip
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mt-1 mb-4`}>
                    Some carriers require that they are listed as{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color={colors.BASICS_BLACK}
                      fontWeight={500}
                    >
                      additional insured
                    </Typography>{' '}
                    on your policy. To expedite your approval, upload coverage documents that
                    include this designation whenever possible.
                  </Typography>
                  <Typography variant="h5" css={tw`mb-1`}>
                    Disclaimer
                  </Typography>
                  <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mt-1`}>
                    Not all carriers require every type of coverage listed above.{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color={colors.BASICS_BLACK}
                      fontWeight={500}
                    >
                      Please check with the carrier you're onboarding with to confirm which
                      insurance documents are necessary.
                    </Typography>{' '}
                  </Typography>
                </div>
              </div>
              <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-6 mt-4`}>
                Clear, complete uploads help avoid delays in the approval process.
              </Typography>
              <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-1.5`}>
                Upload file*
              </Typography>
              <FormikFileInput name="file" />
            </div>
          </div>
          <div css={tw`px-4 py-3`}>
            <Button
              variant="contained"
              size="small"
              loading={isLoading}
              className={tw`w-full`}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
